import type { GameStateData } from '../game/GameState.js';
import { handPips } from '../game/scoring/fivesScoring.js';
import { DOUBLE_BLANK, DOUBLE_SIX, handValue } from '../game/scoring/handValue.js';
import { isDouble, tileEquals, tilePips, type Pip, type Tile } from '../game/types.js';
import type { DominoRuleSet } from '../game/variants/types.js';
import { chainEndPips, coveredEnds, distinctSuits, handWithout, partnerOf } from './kozyolFeatures.js';
import type { PlayMove } from './MoveEnumerator.js';
import { deriveConstraints } from './PassConstraints.js';
import { suitCountsFor, type SuitCounts } from './SuitTracker.js';
import type { MoveScorer } from './types.js';

/**
 * Linear play evaluator for Козёл (penalty scoring). Nothing scores during
 * play — every pip still in hand when the hand ends becomes penalty — so the
 * features are about shedding weight, dodging the 25/50 specials, keeping
 * our own options open and choking the next opponent. The fives
 * `MoveEnumerator.score` is deliberately ignored: it means nothing here.
 *
 * The 1-ply lookahead Medium gets in fives (opponent reply exposure) is
 * folded into the blocking feature: ends the next seat is known to lack, or
 * whose suit is nearly exhausted, are the ones it cannot answer.
 */

/** Per pip of the played tile — leftover pips are the penalty. */
const W_PIP_SHED = 1;
/** Doubles follow only one suit, so they strand easily; shed them early. */
const W_DOUBLE_SHED = 3;
/** [6|6] alone counts 50 instead of 12 — get rid of it whenever possible. */
const W_DOUBLE_SIX_DUMP = 40;
/** Scales the specials surcharge (handValue − raw pips) of the hand left behind. */
const W_SPECIAL_SURCHARGE = 1;
/** Holding [0|0] this late risks ending with it alone (25). */
const W_DOUBLE_BLANK_LATE = -8;
const LATE_HAND_SIZE = 2;
/** Near-terminal: the hand ends and we record nothing. */
const W_GOING_OUT = 1000;
const W_SUIT_DIVERSITY = 1;
/** Per open end we can still follow next turn. */
const W_END_COVERAGE = 3;
/** Per open end the next seat is known (from passes) to lack. */
const W_BLOCK_KNOWN = 8;
/** Per missing unseen copy below the threshold on an open end's suit. */
const W_BLOCK_EXHAUSTED = 2;
const EXHAUST_THRESHOLD = 4;
/** 2v2: per open end the partner is known to lack. */
const W_PARTNER_LACKS = -6;
/** 2v2: per unseen copy (capped) of an open end's suit the partner may hold. */
const W_PARTNER_HOLDS = 1;
const PARTNER_HOLDS_CAP = 2;

export class KozyolEvaluator implements MoveScorer {
  constructor(private readonly ruleSet: DominoRuleSet) {}

  score(state: GameStateData, seat: number, move: PlayMove): number {
    const handAfter = handWithout(state.hands[seat] ?? [], move.tile);
    let total = tilePips(move.tile) * W_PIP_SHED;
    if (isDouble(move.tile)) total += W_DOUBLE_SHED;
    total += specialRisk(move.tile, handAfter);
    if (handAfter.length === 0) return total + W_GOING_OUT;

    const ends = chainEndPips(move.chainAfter);
    total += W_SUIT_DIVERSITY * distinctSuits(handAfter);
    total += W_END_COVERAGE * coveredEnds(ends, handAfter);

    // The played tile moves from our hand to the chain, so `unseen` is the
    // same before and after the play — count once from the current state.
    const counts = suitCountsFor(state, seat);
    const constraints = deriveConstraints(state);
    const nextSeat = (seat + 1) % state.playerCount;
    total += blockScore(ends, constraints[nextSeat], counts);

    if (this.ruleSet.partnership) {
      const partner = partnerOf(state, seat);
      if (partner >= 0) total += partnerScore(ends, constraints[partner], counts);
    }
    return total;
  }
}

function specialRisk(tile: Tile, handAfter: readonly Tile[]): number {
  let risk = 0;
  if (tileEquals(tile, DOUBLE_SIX)) risk += W_DOUBLE_SIX_DUMP;
  risk -= W_SPECIAL_SURCHARGE * (handValue(handAfter) - handPips(handAfter));
  const blankKept = handAfter.some((t) => tileEquals(t, DOUBLE_BLANK));
  if (blankKept && handAfter.length <= LATE_HAND_SIZE) risk += W_DOUBLE_BLANK_LATE;
  return risk;
}

function blockScore(
  ends: readonly Pip[],
  nextLacks: ReadonlySet<Pip> | undefined,
  counts: Record<Pip, SuitCounts>,
): number {
  let score = 0;
  for (const pip of ends) {
    if (nextLacks?.has(pip)) score += W_BLOCK_KNOWN;
    score += W_BLOCK_EXHAUSTED * Math.max(0, EXHAUST_THRESHOLD - counts[pip].unseen);
  }
  return score;
}

function partnerScore(
  ends: readonly Pip[],
  partnerLacks: ReadonlySet<Pip> | undefined,
  counts: Record<Pip, SuitCounts>,
): number {
  let score = 0;
  for (const pip of ends) {
    if (partnerLacks?.has(pip)) score += W_PARTNER_LACKS;
    else score += W_PARTNER_HOLDS * Math.min(PARTNER_HOLDS_CAP, counts[pip].unseen);
  }
  return score;
}
